import { useMemo } from "react";
import { useOrgContext } from "@/hooks/use-org-context";
import { useEntityTypes } from "@/lib/api/entities";
import { isSystemEntityType } from "@/lib/reserved";

/** A type this workspace reaches: one it owns, or a public catalog type. */
export function isWorkspaceEntityType(
	type: { organization_slug?: string | null },
	ownerSlug: string,
): boolean {
	return !type.organization_slug || type.organization_slug === ownerSlug;
}

/**
 * The workspace's entity types, split the way every type list shows them: its
 * own and public types (`visible`), and the reserved `$…` ones (`system`), each
 * dropping types another org owns. Shared by the Data panel, the type page's
 * switcher and the memory scope-bar Type dropdown, so all list the same types.
 */
export function useVisibleEntityTypes(ownerSlug: string) {
	const { orgContext } = useOrgContext();
	const { data: entityTypes = [], isLoading: isTypesLoading } =
		useEntityTypes(orgContext);

	const { visible, system } = useMemo(() => {
		const scoped = entityTypes.filter((et) =>
			isWorkspaceEntityType(et, ownerSlug),
		);
		return {
			visible: scoped.filter((et) => !isSystemEntityType(et)),
			system: scoped.filter((et) => isSystemEntityType(et)),
		};
	}, [entityTypes, ownerSlug]);

	return { visible, system, isTypesLoading };
}
